import {createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {withdrawalPaymentVendors} from '@bdswiss/common-enums'

import AppContext from '../components/Common/contexts/AppContext'
import {VideoVerificationDialog} from './VideoVerificationDialog'
import {useSumsubSession, useVideoVerification, VideoVerificationData} from './hooks'
import {
  HeldWithdrawal,
  screenForSdkError,
  screenForSdkMessage,
  screenForStatus,
  summarizeHeldWithdrawals,
  VideoVerificationScreen,
} from './utils'

type OpenOptions = {
  /** The withdrawals that were just created. They come before the query has seen them. */
  withdrawals?: HeldWithdrawal[]
}

type VideoVerificationContextValue = VideoVerificationData & {
  openVideoVerification: (options?: OpenOptions) => void
}

const noop = () => undefined

const VideoVerificationContext = createContext<VideoVerificationContextValue>({
  loading: false,
  required: false,
  available: false,
  status: null,
  hasHeldWithdrawals: false,
  heldWithdrawals: [],
  refetch: () => Promise.resolve(),
  openVideoVerification: noop,
})

const useVideoVerificationContext = () => useContext(VideoVerificationContext)

type DialogState = {screen: VideoVerificationScreen; withdrawals: HeldWithdrawal[]; token: string | null}

/** Holds the state of the client and the one pop-up that every screen of the Dashboard opens. */
function VideoVerificationProvider({children}: {children: ReactNode}): ReactElement {
  const {clientId, locale} = useContext(AppContext) as {clientId?: number; locale: string}
  const history = useHistory()
  const data = useVideoVerification(!clientId)
  const {startSession, fetchToken} = useSumsubSession()
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const {refetch, status, heldWithdrawals} = data

  const openVideoVerification = useCallback(
    (options?: OpenOptions) => {
      refetch()
      setDialog({
        screen: screenForStatus(status),
        withdrawals: options?.withdrawals?.length ? options.withdrawals : heldWithdrawals,
        token: null,
      })
    },
    [refetch, status, heldWithdrawals],
  )

  const showScreen = useCallback(
    (screen: VideoVerificationScreen) => setDialog((current) => (current ? {...current, screen, token: null} : current)),
    [],
  )

  // Escape, the close button and "Do it later" leave the withdrawal on hold.
  const close = useCallback(() => {
    setDialog(null)
    refetch()
  }, [refetch])

  const start = useCallback(async () => {
    setIsBusy(true)
    try {
      const token = await startSession()
      setDialog((current) => (current ? {...current, screen: 'sumsub', token} : current))
    } catch {
      showScreen('down')
    } finally {
      setIsBusy(false)
    }
  }, [startSession, showScreen])

  const handleSdkMessage = useCallback(
    (type: string, payload: any) => {
      const next = screenForSdkMessage(type, payload)
      if (!next) return
      showScreen(next)
      refetch()
    },
    [showScreen, refetch],
  )

  const handleSdkError = useCallback((error) => showScreen(screenForSdkError(error)), [showScreen])

  const contactSupport = useCallback(() => {
    close()
    history.push('/support')
  }, [close, history])

  const {loading, required, available, hasHeldWithdrawals} = data
  const value = useMemo(
    () => ({loading, required, available, status, hasHeldWithdrawals, heldWithdrawals, refetch, openVideoVerification}),
    [loading, required, available, status, hasHeldWithdrawals, heldWithdrawals, refetch, openVideoVerification],
  )

  const summary = useMemo(
    () =>
      dialog?.withdrawals.length
        ? summarizeHeldWithdrawals(dialog.withdrawals, locale, (vendor) => {
            const method = withdrawalPaymentVendors[vendor]
            return method ? method.localization.t(locale) : vendor
          })
        : null,
    [dialog?.withdrawals, locale],
  )

  return (
    <VideoVerificationContext.Provider value={value}>
      {children}
      {dialog ? (
        <VideoVerificationDialog
          screen={dialog.screen}
          summary={summary}
          sumsub={
            dialog.token
              ? {
                  token: dialog.token,
                  locale,
                  fetchToken,
                  onMessage: handleSdkMessage,
                  onError: handleSdkError,
                }
              : null
          }
          isBusy={isBusy}
          onStart={start}
          onTryAgain={start}
          onContactSupport={contactSupport}
          onClose={close}
        />
      ) : null}
    </VideoVerificationContext.Provider>
  )
}

export {VideoVerificationProvider, VideoVerificationContext, useVideoVerificationContext}
